import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

type StripeEnv = "test" | "live";
type Audience = "family" | "company";
type Interval = "month" | "year";

interface PreviewItem {
  stripe_product_id: string;
  stripe_price_id: string;
  product_name: string | null;
  interval: Interval;
  unit_amount: number | null;
  currency: string | null;
  nickname: string | null;
  suggested_audience: Audience;
  suggested_label: string;
}
interface UpdateItem extends PreviewItem {
  local_id: string;
  current_audience: Audience;
  current_label: string | null;
  current_product_id: string;
  changed_fields: string[];
}
interface OrphanItem {
  local_id: string;
  stripe_price_id: string;
  stripe_product_id: string;
  audience: Audience;
  interval: Interval;
  label: string | null;
}

interface Preview {
  env: StripeEnv;
  toCreate: PreviewItem[];
  toUpdate: UpdateItem[];
  toDeactivate: OrphanItem[];
}

interface CreateRow extends PreviewItem {
  _selected: boolean;
  _audience: Audience;
  _label: string;
}
interface UpdateRow extends UpdateItem {
  _selected: boolean;
  _audience: Audience;
  _label: string;
}
interface OrphanRow extends OrphanItem {
  _selected: boolean;
}

function fmtAmount(amount: number | null, currency: string | null) {
  if (amount == null || !currency) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: currency.toUpperCase() }).format(amount / 100);
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  env: StripeEnv;
  onComplete: () => void;
}

export function StripeSyncDialog({ open, onOpenChange, env, onComplete }: Props) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [creates, setCreates] = useState<CreateRow[]>([]);
  const [updates, setUpdates] = useState<UpdateRow[]>([]);
  const [orphans, setOrphans] = useState<OrphanRow[]>([]);

  useEffect(() => {
    if (!open) return;
    void loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, env]);

  async function loadPreview() {
    setLoading(true);
    setPreview(null);
    const { data, error } = await supabase.functions.invoke("sync-stripe-prices", {
      body: { env, mode: "preview" },
    });
    setLoading(false);
    if (error || data?.error) {
      const desc = [data?.where && `[${data.where}]`, error?.message ?? data?.error].filter(Boolean).join(" ");
      toast.error("Falha ao carregar prévia", { description: desc });
      return;
    }
    const p = data as Preview;
    setPreview(p);
    setCreates(p.toCreate.map((i) => ({ ...i, _selected: true, _audience: i.suggested_audience, _label: i.suggested_label })));
    setUpdates(p.toUpdate.map((i) => ({ ...i, _selected: true, _audience: i.current_audience, _label: i.current_label ?? i.suggested_label })));
    setOrphans(p.toDeactivate.map((i) => ({ ...i, _selected: true })));
  }

  async function apply() {
    setApplying(true);
    const payload = {
      env,
      mode: "apply",
      creates: creates.filter((c) => c._selected).map((c) => ({
        stripe_product_id: c.stripe_product_id,
        stripe_price_id: c.stripe_price_id,
        interval: c.interval,
        audience: c._audience,
        label: c._label || null,
      })),
      updates: updates.filter((u) => u._selected).map((u) => ({
        local_id: u.local_id,
        stripe_product_id: u.stripe_product_id,
        interval: u.interval,
        audience: u._audience,
        label: u._label || null,
      })),
      deactivates: orphans.filter((o) => o._selected).map((o) => ({ local_id: o.local_id })),
    };
    const { data, error } = await supabase.functions.invoke("sync-stripe-prices", { body: payload });
    setApplying(false);
    if (error || data?.error) {
      const desc = [data?.where && `[${data.where}]`, error?.message ?? data?.error].filter(Boolean).join(" ");
      toast.error("Falha ao aplicar", { description: desc });
      return;
    }
    const { created = 0, updated = 0, deactivated = 0, errors = [] } = data ?? {};
    if (errors.length > 0) {
      toast.warning(`Parcial: ${created} criados, ${updated} atualizados, ${deactivated} desativados`, {
        description: errors.join("; "),
      });
    } else {
      toast.success(`${created} criados · ${updated} atualizados · ${deactivated} desativados`);
    }
    onComplete();
    onOpenChange(false);
  }

  const totalSelected =
    creates.filter((c) => c._selected).length +
    updates.filter((u) => u._selected).length +
    orphans.filter((o) => o._selected).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar preços do Stripe — {env.toUpperCase()}</DialogTitle>
          <DialogDescription>
            Revise os preços encontrados, ajuste a audiência e label, e aplique somente os selecionados.
            Itens marcados como "padrão" devem ser definidos manualmente após a importação.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : !preview ? null : (
          <Tabs defaultValue="create">
            <TabsList>
              <TabsTrigger value="create">Novos ({creates.length})</TabsTrigger>
              <TabsTrigger value="update">Atualizar ({updates.length})</TabsTrigger>
              <TabsTrigger value="deactivate">Desativar ({orphans.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="create" className="mt-4">
              {creates.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">Nenhum preço novo no Stripe.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Produto / Preço</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead>Intervalo</TableHead>
                      <TableHead>Audiência</TableHead>
                      <TableHead>Label</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {creates.map((c, idx) => (
                      <TableRow key={c.stripe_price_id}>
                        <TableCell>
                          <Checkbox
                            checked={c._selected}
                            onCheckedChange={(v) => {
                              const copy = [...creates]; copy[idx] = { ...copy[idx], _selected: !!v }; setCreates(copy);
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          <div>{c.product_name ?? "—"}</div>
                          <div className="text-muted-foreground">{c.stripe_price_id}</div>
                        </TableCell>
                        <TableCell>{fmtAmount(c.unit_amount, c.currency)}</TableCell>
                        <TableCell>{c.interval}</TableCell>
                        <TableCell>
                          <Select
                            value={c._audience}
                            onValueChange={(v) => {
                              const copy = [...creates]; copy[idx] = { ...copy[idx], _audience: v as Audience }; setCreates(copy);
                            }}
                          >
                            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="family">Família</SelectItem>
                              <SelectItem value="company">Empresa</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={c._label}
                            onChange={(e) => {
                              const copy = [...creates]; copy[idx] = { ...copy[idx], _label: e.target.value }; setCreates(copy);
                            }}
                            className="min-w-[200px]"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="update" className="mt-4">
              {updates.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">Nenhuma divergência encontrada.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Preço</TableHead>
                      <TableHead>Alterações</TableHead>
                      <TableHead>Audiência</TableHead>
                      <TableHead>Label</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {updates.map((u, idx) => (
                      <TableRow key={u.stripe_price_id}>
                        <TableCell>
                          <Checkbox
                            checked={u._selected}
                            onCheckedChange={(v) => {
                              const copy = [...updates]; copy[idx] = { ...copy[idx], _selected: !!v }; setUpdates(copy);
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs">{u.stripe_price_id}</TableCell>
                        <TableCell className="space-x-1">
                          {u.changed_fields.map((f) => <Badge key={f} variant="outline">{f}</Badge>)}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={u._audience}
                            onValueChange={(v) => {
                              const copy = [...updates]; copy[idx] = { ...copy[idx], _audience: v as Audience }; setUpdates(copy);
                            }}
                          >
                            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="family">Família</SelectItem>
                              <SelectItem value="company">Empresa</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={u._label}
                            onChange={(e) => {
                              const copy = [...updates]; copy[idx] = { ...copy[idx], _label: e.target.value }; setUpdates(copy);
                            }}
                            className="min-w-[200px]"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="deactivate" className="mt-4">
              {orphans.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">Nenhum preço órfão.</p>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-2">
                    Estes preços estão ativos localmente mas não estão mais ativos no Stripe. Sugestão: desativar (não exclui).
                  </p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead>Preço</TableHead>
                        <TableHead>Audiência</TableHead>
                        <TableHead>Intervalo</TableHead>
                        <TableHead>Label</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orphans.map((o, idx) => (
                        <TableRow key={o.local_id}>
                          <TableCell>
                            <Checkbox
                              checked={o._selected}
                              onCheckedChange={(v) => {
                                const copy = [...orphans]; copy[idx] = { ...copy[idx], _selected: !!v }; setOrphans(copy);
                              }}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-xs">{o.stripe_price_id}</TableCell>
                          <TableCell><Badge variant="outline">{o.audience}</Badge></TableCell>
                          <TableCell>{o.interval}</TableCell>
                          <TableCell className="text-sm">{o.label ?? "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying}>Cancelar</Button>
          <Button onClick={apply} disabled={applying || loading || totalSelected === 0}>
            {applying ? "Aplicando..." : `Aplicar ${totalSelected} ${totalSelected === 1 ? "item" : "itens"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
